import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { useSupabase } from '../contexts/SupabaseContext';

export function usePageTracking(sessionId: string | null) {
  const { user } = useAuth();
  const { supabase } = useSupabase();
  const location = useLocation();
  const pageStartTimeRef = useRef<number>(Date.now());
  const currentPageRef = useRef<string>(location.pathname);
  const previousPageViewIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user || !sessionId) return;

    // Update time on previous page if it exists
    if (previousPageViewIdRef.current && currentPageRef.current !== location.pathname) {
      updateTimeOnPage();
    }

    // Track new page view
    trackPageView();

    // Update refs
    currentPageRef.current = location.pathname;
    pageStartTimeRef.current = Date.now();

    // Cleanup: update time on page when component unmounts
    return () => {
      if (previousPageViewIdRef.current) {
        updateTimeOnPage();
      }
    };
  }, [location.pathname, user, sessionId]);

  const trackPageView = async () => {
    try {
      const pageTitle = document.title;

      const { data, error } = await supabase.rpc('track_page_view', {
        p_session_id: sessionId,
        p_page_path: location.pathname,
        p_page_title: pageTitle,
        p_time_on_page_seconds: null,
      });

      if (error) {
        console.error('Error tracking page view:', error);
        return;
      }

      previousPageViewIdRef.current = data as string;
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  };

  const updateTimeOnPage = async () => {
    if (!previousPageViewIdRef.current) return;

    const timeOnPage = Math.floor((Date.now() - pageStartTimeRef.current) / 1000);

    try {
      await supabase
        .from('page_views')
        .update({ time_on_page_seconds: timeOnPage })
        .eq('id', previousPageViewIdRef.current);
    } catch (error) {
      console.error('Error updating time on page:', error);
    }
  };

  return null;
}
